import type { MetricsMenuItem } from './components/MetricsMenu/MetricsMenu';
import type { MenuLocale } from '@/features/menu/constants/menuLabels';
import { channelMenuItems } from './channelMenuItems';
import { getMenuLocale } from '@/features/menu/constants/menuItems';
import ptTranslations from '@/locales/pt.json';
import enTranslations from '@/locales/en.json';
import esTranslations from '@/locales/es.json';
import {
  getMetricsMenuItemPath,
  staticMetricsPaths,
  type MetricsMenuChannel,
} from './metricsMenuPaths';

export type MetricsChannel = MetricsMenuChannel;

export const isMetricsChannel = (channel: string): channel is MetricsChannel =>
  Object.hasOwn(channelMenuItems, channel);

// These pages have no menu, so they open in a new tab and keep the current page untouched.
const NEW_TAB_ITEM_KEYS = new Set(['allComments']);

type MetricsMenuLabelKey = keyof typeof ptTranslations.metricsMenu;
type MetricsMenuNode = {
  key: string;
  label: MetricsMenuLabelKey;
  children?: MetricsMenuNode[];
  defaultOpen?: boolean;
};

const metricsMenuTranslations: Record<MenuLocale, Record<MetricsMenuLabelKey, string>> = {
  pt: ptTranslations.metricsMenu,
  en: enTranslations.metricsMenu,
  es: esTranslations.metricsMenu,
};

const filterMenuItems = (items: MetricsMenuItem[], itemIds?: string[]): MetricsMenuItem[] => {
  if (!itemIds || itemIds.length === 0) {
    return items;
  }

  const allowedIds = new Set(itemIds);

  return items.reduce<MetricsMenuItem[]>((accumulator, item) => {
    const children = item.children ? filterMenuItems(item.children, itemIds) : [];
    const shouldInclude = allowedIds.has(item.id) || children.length > 0;

    if (shouldInclude) {
      accumulator.push({
        ...item,
        children,
      });
    }

    return accumulator;
  }, []);
};

const applyDisabledItems = (items: MetricsMenuItem[], disabled: Set<string>): MetricsMenuItem[] =>
  items.map((item) => {
    const children = item.children ? applyDisabledItems(item.children, disabled) : undefined;
    return {
      ...item,
      ...(children ? { children } : {}),
      ...(disabled.has(item.id) ? { disabled: true, disabledReason: 'permission' as const } : {}),
    };
  });

export const getMetricsMenuItems = (
  locale: MenuLocale,
  channel: MetricsChannel = 'facebook',
  itemIds?: string[],
  disabledItemIds?: string[],
): MetricsMenuItem[] => {
  if (!isMetricsChannel(channel)) {
    throw new Error(`Unsupported metrics channel: "${channel}"`);
  }

  const labels = metricsMenuTranslations[locale] ?? metricsMenuTranslations.pt;

  const commonItems: MetricsMenuItem[] = [
    {
      id: 'my-connections',
      label: labels.myConnections,
      path: staticMetricsPaths.myConnections,
      href: staticMetricsPaths.myConnections,
    },
    {
      id: 'conversations-report',
      label: labels.conversationsReport,
      path: staticMetricsPaths.conversationsReport,
      href: staticMetricsPaths.conversationsReport,
    },
    {
      id: 'settings',
      label: labels.settings,
      path: staticMetricsPaths.settings,
      href: staticMetricsPaths.settings,
    },
  ];

  const buildChannelItem = (node: MetricsMenuNode): MetricsMenuItem => {
    const children = node.children?.map(buildChannelItem);
    const path = children?.length ? undefined : getMetricsMenuItemPath(channel, node.key);

    return {
      id: `${channel}-${node.key}`,
      label: labels[node.label],
      ...(path ? { path, href: path } : {}),
      ...(path && NEW_TAB_ITEM_KEYS.has(node.key) ? { target: '_blank' } : {}),
      ...(children ? { children } : {}),
      ...(node.defaultOpen === undefined ? {} : { defaultOpen: node.defaultOpen }),
    };
  };

  const channelItem: MetricsMenuItem = {
    id: channel,
    label: labels[channel],
    ...(channel === 'facebook' ? { defaultOpen: true } : {}),
    children: channelMenuItems[channel].map(buildChannelItem),
  };

  const filtered = filterMenuItems([...commonItems, channelItem], itemIds);
  if (!disabledItemIds?.length) return filtered;

  return applyDisabledItems(filtered, new Set(disabledItemIds));
};

export const getDefaultMetricsMenuItems = (itemIds?: string[]): MetricsMenuItem[] =>
  getMetricsMenuItems(getMenuLocale(), 'facebook', itemIds);